// The monitoring policy as staff meet it (R22, R24; B55). One page: the version in force,
// in the company's own words, and a button that records having read it. In U20's look since
// B202 (milestone 5 check 5): a panel with its rule line, the words whole, a pill button.
// Since U129 the words are drawn with their structure: a title, sections to jump between,
// lists as lists (rails/PolicyWords.tsx). Structure only; not a word is added or left out.
//
// Nothing here is Cobrain's opinion of the policy, and nothing summarises it: the words
// are the company's and are shown whole. A draft never reaches this page, because the
// select policy on rails.monitoring_policy shows an unpublished version to an admin alone
// (migration 34) — so "no policy yet" here means there is no policy yet, not that one is
// being kept from the reader.
import { ActionButton, CardFrame, SectionHeading } from './parts/index.js';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, Unauthenticated, type Store } from './api.js';
import { formatDate } from './labels.js';
import { railsMe, type PolicyView } from './rails.js';
import { PolicyWords } from './rails/PolicyWords.js';
import './rails-admin.css';

export function Policy({ store }: { store: Store }) {
  const [state, setState] = useState<{ kind: 'loading' } | { kind: 'ready'; policy: PolicyView | null } | { kind: 'error'; message: string }>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    railsMe.policy(store.tenantId)
      .then((r) => setState({ kind: 'ready', policy: r.policy }))
      .catch((e: unknown) => {
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        setState({ kind: 'error', message: e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e) });
      });
  }, [store.tenantId]);
  useEffect(load, [load]);

  if (state.kind === 'loading') return <div className="rails-policy"><p className="meta pad">Reading the policy…</p></div>;
  if (state.kind === 'error') return <div className="rails-policy"><p className="plain pad">Cobrain could not read the policy just now. {state.message}</p></div>;
  if (state.policy === null) {
    return (
      <div className="rails-policy">
        <SectionHeading title={<>Monitoring policy</>} />
        <CardFrame variant="panel">
          <h2>There is no monitoring policy published yet.</h2>
          <p className="rule">When there is one, it appears here and you will be asked to confirm you have read it.</p>
        </CardFrame>
      </div>
    );
  }

  const p = state.policy;
  const ack = () => {
    setBusy(true);
    railsMe.ackPolicy(store.tenantId, p.id)
      .then((r) => setState({ kind: 'ready', policy: r.policy }))
      .catch((e: unknown) => setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) }))
      .finally(() => setBusy(false));
  };

  return (
    <div className="rails-policy">
      <SectionHeading title={<>Monitoring policy</>} note={<>version {p.versionNo}</>} />
      <CardFrame variant="panel">
        <p className="meta">
          Version {p.versionNo}, published {formatDate(p.publishedAt!)}{p.publishedBy ? ` by ${p.publishedBy}` : ''}.
        </p>
        {/* The company's words, whole and unsummarised, with the structure the author gave them (U129). */}
        <PolicyWords body={p.body} />
        {p.ackedByMe
          ? <p className="note">You have confirmed you read this version.</p>
          : <ActionButton className="btn" type="button" disabled={busy} onClick={ack}>I have read this</ActionButton>}
      </CardFrame>
    </div>
  );
}
